/**
 * NosAKTopBar — Global operational top bar for Nosak workspace.
 * Command search, period indicator, notifications, system status.
 */
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useNosak } from "@/context/NOSAKContext";
import { getBadges } from "@/context/NosAKBadgeStore";
import { Search, Bell, Download, ChevronDown, Activity } from "lucide-react";

const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };

export default function NosAKTopBar() {
  const { group, subsidiaries, projects, selectedPeriod, periods, setSelectedPeriod, loading } = useNosak();
  const navigate = useNavigate();
  const location = useLocation();
  const [search, setSearch] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const searchRef = useRef(null);
  const badges = getBadges();
  const totalBadges = (badges.financeReady || 0) + (badges.evidenceGaps || 0) + (badges.highRisks || 0);

  // Build searchable index from SSOT data
  const searchIndex = [
    ...(subsidiaries || []).map(s => ({
      type: "entity", label: s.name, sub: s.division?.replace(/_/g," ") || "",
      value: `${fmtN(s.total_tco2e)} tCO₂e/yr`, path: "/nosak/intelligence",
    })),
    ...(projects?.list || []).map(p => ({
      type: "project", label: p.name?.slice(0,40), sub: p.type?.replace(/_/g," ") || "",
      value: `$${fmtN(p.capex_usd)} · ${p.status}`, path: "/nosak/finance",
    })),
    { type: "page", label: "Executive Dashboard",        sub: "KPIs · waterfall · actions",  path: "/nosak/dashboard"     },
    { type: "page", label: "Data Workspace (SSOT)",      sub: "Input engine · calculate",     path: "/nosak/workspace"     },
    { type: "page", label: "Climate & Circularity",      sub: "Analytics · intelligence",     path: "/nosak/intelligence"  },
    { type: "page", label: "Finance & Carbon Markets",   sub: "Green bond · carbon pathways", path: "/nosak/finance"       },
    { type: "page", label: "ESG, Risk & Disclosure",     sub: "IFRS S2 · TCFD · risk",        path: "/nosak/esg"           },
    { type: "page", label: "Evidence Vault & Registry",  sub: "MRV · VVB readiness",          path: "/nosak/evidence"      },
    { type: "page", label: "Settings & Administration",  sub: "Governance · methodology",     path: "/nosak/settings"      },
  ];

  const results = search.length > 1
    ? searchIndex.filter(item =>
        (item.label || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.sub || "").toLowerCase().includes(search.toLowerCase())
      ).slice(0, 8)
    : [];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Notifications derived from SSOT
  const notifications = [
    badges.highRisks > 0 && { level: "high", text: `Data confidence below threshold (${group.data_confidence_avg?.toFixed(0)}/100). ${badges.evidenceGaps} entities need T1 upgrade.`, path: "/nosak/evidence" },
    badges.financeReady > 0 && { level: "info", text: `${badges.financeReady} projects ready for DFI / green bond pre-engagement.`, path: "/nosak/finance" },
    (group.circularity_score_avg || 0) < 55 && { level: "warn", text: `Circularity score (${group.circularity_score_avg?.toFixed(0)}/100) below 65 target. Biomass valorisation gaps present.`, path: "/nosak/intelligence" },
    badges.boardAlerts > 0 && { level: "high", text: "Board attention: operational data quality requires immediate upgrade for external disclosure.", path: "/nosak/settings" },
  ].filter(Boolean);

  const TYPE_COLOR = { entity: "#059669", project: "#1d4ed8", page: "#64748b" };

  return (
    <div
      className="flex items-center justify-between px-5 py-2 border-b border-slate-800 bg-[#0a0f1a]"
      style={{ height: "46px" }}
      data-testid="nosak-topbar"
    >
      {/* Left: workspace identity */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">NOSAK</span>
        </div>
        <div className="w-px h-4 bg-slate-700" />
        {/* Period selector */}
        <select
          value={selectedPeriod}
          onChange={e => setSelectedPeriod(e.target.value)}
          className="text-[10px] font-bold text-slate-300 bg-transparent border-none outline-none cursor-pointer"
          data-testid="topbar-period"
        >
          {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* Center: command search */}
      <div className="flex-1 max-w-md mx-4 relative" ref={searchRef}>
        <div className="flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-lg px-3 py-1.5">
          <Search size={11} className="text-slate-500 flex-shrink-0" />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            placeholder="Search entities, projects, pages…"
            className="flex-1 text-xs bg-transparent text-slate-300 placeholder:text-slate-600 outline-none"
            data-testid="command-search"
          />
          <span className="text-[9px] text-slate-600 bg-slate-700 px-1.5 py-0.5 rounded font-mono">⌘K</span>
        </div>

        {/* Search results */}
        {showResults && results.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
            {results.map((r, i) => (
              <button key={i} onClick={() => { navigate(r.path); setSearch(""); setShowResults(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800 text-left transition-colors"
                data-testid={`search-result-${i}`}>
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: TYPE_COLOR[r.type] || "#94a3b8" }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-200 truncate">{r.label}</p>
                  <p className="text-[9px] text-slate-500">{r.sub}</p>
                </div>
                <p className="text-[9px] text-slate-500 flex-shrink-0">{r.value}</p>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Right: status + notifications */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* System status */}
        {!loading && (
          <div className="flex items-center gap-1.5 text-[9px] text-emerald-500 bg-emerald-900/20 border border-emerald-800/40 px-2 py-1 rounded">
            <Activity size={9} />
            <span>SSOT Live</span>
          </div>
        )}

        {/* Quick stats */}
        <div className="hidden sm:flex items-center gap-1 text-[9px] text-slate-500">
          <span>{fmtN(group.total_tco2e)} tCO₂e</span>
          <span className="text-slate-700">·</span>
          <span className="text-emerald-500">{fmtN(group.reduction_potential_tco2e)} reducible</span>
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setShowNotifs(!showNotifs)}
            className="relative flex items-center justify-center w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 transition-colors"
            data-testid="notification-btn"
          >
            <Bell size={12} className="text-slate-400" />
            {totalBadges > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full text-[7px] font-black text-white flex items-center justify-center">
                {totalBadges}
              </span>
            )}
          </button>

          {showNotifs && (
            <div className="absolute top-full right-0 mt-1 w-80 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
              <div className="px-4 py-2 border-b border-slate-800">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Operational Alerts</p>
              </div>
              {notifications.length === 0 ? (
                <p className="text-xs text-slate-500 px-4 py-3">No active alerts</p>
              ) : notifications.map((n, i) => (
                <button key={i} onClick={() => { navigate(n.path); setShowNotifs(false); }}
                  className={`w-full flex items-start gap-2.5 px-4 py-2.5 hover:bg-slate-800 text-left border-b border-slate-800/50 last:border-0`}>
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${
                    n.level === "high" ? "bg-red-500" : n.level === "warn" ? "bg-amber-500" : "bg-blue-500"
                  }`} />
                  <p className="text-[10px] text-slate-300 leading-relaxed">{n.text}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
